import { AssetDetail, AssetVersionListItem } from '../../types'
import { Button } from '@/components/ui/button'
import { Link, Calendar, Building2, User, Pencil } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { StatusPublished } from '@/modules/Asset/constants'

import { formatVersion } from '@/modules/Asset/utils/version-bump'
import AssetStatusBadge from '../AssetStatusBadge'

interface AssetHeaderProps {
    asset: AssetDetail
    versions?: AssetVersionListItem[]
    selectedVersionId?: string
    onVersionChange?: (versionId: string) => void
}

const AssetHeader = ({
    asset,
    versions,
    selectedVersionId,
    onVersionChange,
}: AssetHeaderProps) => {
    const { t } = useTranslation('asset')
    const navigate = useNavigate()

    const isPublished =
        asset.current_version?.asset_version_status === StatusPublished
    const version = asset.current_version

    const handleCopyLink = () => {
        const url = window.location.href
        navigator.clipboard.writeText(url).then(() => {
            toast.success(t('messages.copyLinkSuccess'))
        })
    }

    return (
        <div
            className="bg-white rounded-lg shadow-sm"
            data-tour="asset-header-info"
        >
            {/* Top row: title + actions */}
            <div className="flex items-start justify-between gap-4 p-6 pb-4">
                <div className="min-w-0 flex-1 space-y-2">
                    {/* Title */}
                    <h1 className="text-2xl font-bold text-gray-900 truncate">
                        {asset.asset_name}
                    </h1>

                    {/* Badges row */}
                    <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className="text-xs font-mono">
                            {asset.asset_slug}
                        </Badge>
                        {version && (
                            <AssetStatusBadge
                                status={version.asset_version_status}
                                className="text-xs"
                            />
                        )}
                        {versions && versions.length > 0 && onVersionChange ? (
                            <Select
                                value={selectedVersionId}
                                onValueChange={onVersionChange}
                            >
                                <SelectTrigger className="w-auto h-7 text-xs gap-1 font-mono">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {versions.map((v) => (
                                        <SelectItem
                                            key={v.asset_version_id}
                                            value={v.asset_version_id}
                                        >
                                            <span className="font-mono">
                                                {formatVersion(v)}
                                            </span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        ) : (
                            version && (
                                <Badge
                                    variant="outline"
                                    className="text-xs font-mono"
                                >
                                    {formatVersion(version)}
                                </Badge>
                            )
                        )}
                    </div>
                </div>

                {/* Actions */}
                <div className="flex shrink-0 gap-2">
                    {isPublished && (
                        <Button
                            variant="default"
                            className="gap-2"
                            onClick={() =>
                                navigate(
                                    `/assets/${asset.asset_slug}/edit?mode=update`
                                )
                            }
                            data-tour="asset-update"
                        >
                            <Pencil className="h-4 w-4" />
                            {t('actions.update')}
                        </Button>
                    )}
                    <Button
                        variant="outline"
                        className="gap-2"
                        onClick={handleCopyLink}
                        data-tour="asset-copy-link"
                    >
                        <Link className="h-4 w-4" />
                        {t('action.copyLink')}
                    </Button>
                </div>
            </div>

            <Separator />

            {/* Metadata row */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-6 py-3 text-sm text-gray-500">
                <div className="flex items-center gap-1.5">
                    <User className="h-3.5 w-3.5" />
                    <span>{asset.created_by_name}</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <Building2 className="h-3.5 w-3.5" />
                    <span>{asset.department_name}</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5" />
                    <span>
                        {new Date(asset.updated_at).toLocaleDateString()}
                    </span>
                </div>
            </div>
        </div>
    )
}

export default AssetHeader
